<template>
  <!--
    2026-07-20: plain wrapper, not its own <b-card> — InvoiceCreateManual.vue
    already wraps every wizard step in one shared card.

    2026-07-23: card-per-row entry replaces the wide horizontal spreadsheet
    in the narrow wizard column. Compact row summary + vertical field form
    for the active item so Rate / HSN / etc. are never off-screen.
  -->
  <div class="wizard-step-compact line-items-card">
    <!-- Q1: Category + Vertical — stay expanded until both required fields are set -->
    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :class="{ 'wizard-accordion-toggle--locked': !canCollapseCategory }"
        :aria-expanded="isCategorySectionOpen ? 'true' : 'false'"
        :aria-disabled="canCollapseCategory ? 'false' : 'true'"
        :title="canCollapseCategory ? '' : 'Select Category and Vertical first'"
        @click="toggleCategorySection"
      >
        <h5 class="wizard-question-heading">What category and vertical is this invoice for?</h5>
        <feather-icon
          v-if="canCollapseCategory"
          :icon="isCategorySectionOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          size="16"
        />
      </button>
      <b-collapse :visible="isCategorySectionOpen">
      <div class="d-flex align-items-start flex-wrap category-quick-pick-row">
        <div class="category-quick-pick-field">
          <v-select
            id="line-items-category-select"
            class="category-inline-select"
            v-model="formData.category"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="title"
            :options="categories"
            :reduce="o => o.title"
            placeholder="Category *"
            :class="{ 'is-invalid-vs': showCategoryVerticalErrors && !formData.category }"
            @input="onCategoryInput"
          />
        </div>
        <div class="category-quick-pick-field">
          <v-select
            id="line-items-vertical-select"
            class="category-inline-select"
            v-model="formData.vertical"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="title"
            :options="verticalOptions"
            :reduce="o => o.title"
            placeholder="Vertical *"
            :class="{ 'is-invalid-vs': showCategoryVerticalErrors && !formData.vertical }"
            @input="onVerticalInput"
          />
        </div>
      </div>
      <small
        v-if="showCategoryVerticalErrors && categoryVerticalErrorMessage"
        class="text-danger d-block mt-50 wizard-field-error"
      >
        {{ categoryVerticalErrorMessage }}
      </small>
      <small class="wizard-question-hint">
        Sets the line-item format. Candidate selection is only offered for <strong>Recruitment + Service Industry</strong>.
      </small>
      </b-collapse>
    </div>

    <div class="wizard-question wizard-accordion-section" v-if="isCandidateCategory">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'candidates' ? 'true' : 'false'"
        @click="toggleSection('candidates')"
      >
        <h5 class="wizard-question-heading">Is this invoice for candidates?</h5>
        <feather-icon :icon="openSection === 'candidates' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'candidates'">
      <b-form-radio-group
        v-model="isForCandidatesLocal"
        :options="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"
        @change="$emit('candidate-option-changed', $event)"
      />
      <div v-if="isForCandidatesLocal === true" class="mt-1">
      <div class="d-flex justify-content-between align-items-center flex-nowrap">
        <h5 class="wizard-question-heading mb-0 mr-1">Which candidates should become line items?</h5>
        <b-button
          variant="outline-primary"
          size="sm"
          class="flex-shrink-0"
          @click="$emit('open-candidate-modal')"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          Add candidates
        </b-button>
      </div>
      <small v-if="selectedCandidateIds.length > 0" class="wizard-question-hint">
        {{ selectedCandidateIds.length }} candidate(s) added as line items below.
      </small>
      <small v-else class="wizard-question-hint">
        Click “Add Candidates” to search from the ATS, or add manual line items below.
      </small>
      </div>
      </b-collapse>
    </div>

    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'secondTable' ? 'true' : 'false'"
        @click="toggleSection('secondTable')"
      >
        <h5 class="wizard-question-heading">Add a second line items table?</h5>
        <feather-icon :icon="openSection === 'secondTable' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'secondTable'">
      <b-form-checkbox v-model="formData.useSecondLineItemsTable" class="mb-0">
        Yes — use two tables (e.g. by location)
      </b-form-checkbox>
      <div v-if="formData.useSecondLineItemsTable" class="mt-1">
        <b-form-group label="First table heading *" class="mb-1">
          <b-form-input
            v-model="formData.lineItemTableHeading1"
            placeholder="e.g. Fatuha"
            trim
          />
        </b-form-group>
        <b-form-group label="Second table heading *" class="mb-0">
          <b-form-input
            v-model="formData.lineItemTableHeading2"
            placeholder="e.g. Barh"
            trim
          />
        </b-form-group>
        <b-form-group
          label="Are the columns the same for both tables?"
          label-class="font-weight-bold"
          class="mb-0 mt-2"
        >
          <b-form-radio-group
            :checked="formData.secondTableColumnsSame !== false"
            :options="secondTableColumnsSameOptions"
            @change="$emit('second-table-columns-same-change', $event)"
          />
        </b-form-group>
        <small class="wizard-question-hint d-block mt-50">
          Assign each line item to Table 1 or Table 2 below. Headings appear on the invoice.
          <template v-if="formData.secondTableColumnsSame === false">
            Edit Columns separately for each table (Table 1 / Table 2 in the columns modal).
          </template>
          <template v-else>
            Both tables share the same columns.
          </template>
        </small>
      </div>
      </b-collapse>
    </div>

    <!-- Line items: summary list + card editor for active row -->
    <div class="wizard-question wizard-accordion-section line-items-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'lineItems' ? 'true' : 'false'"
        @click="toggleSection('lineItems')"
      >
        <h5 class="wizard-question-heading mb-0 d-flex align-items-center flex-wrap">
          Line items
          <b-badge variant="light-primary" class="ml-1 align-middle preset-name-badge">
            {{ activeTemplateName || 'Standard' }}
          </b-badge>
          <b-spinner v-if="templateLoading" small class="ml-1 align-middle" />
        </h5>
        <feather-icon :icon="openSection === 'lineItems' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <div class="d-flex align-items-center line-items-config-btns">
          <b-button
            v-if="isCandidateCategory && isForCandidatesLocal === true"
            variant="outline-primary"
            size="sm"
            class="mr-50"
            @click.stop="$emit('open-candidate-modal')"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            Add candidates
          </b-button>
          <!-- Preview = read-only sample layout; Columns = Edit Columns editor.
               Must stay distinct — both used to open the same Preview tab. -->
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-50 wizard-icon-btn"
            aria-label="Preview column layout"
            title="Preview"
            @click="$emit('open-line-items-config', 'preview')"
          >
            <feather-icon icon="EyeIcon" size="16" />
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="wizard-icon-btn"
            aria-label="Edit Columns"
            title="Edit Columns"
            @click="$emit('open-line-items-config', 'edit')"
          >
            <feather-icon icon="SettingsIcon" size="16" />
          </b-button>
      </div>
      <b-collapse :visible="openSection === 'lineItems'">
      <div class="li-howto mb-2">
        <p class="wizard-question-hint li-howto-lead mb-0">
          Click a row to edit. Use Preview and Edit Columns to change the layout.
        </p>
        <button
          type="button"
          class="li-howto-toggle"
          :aria-expanded="showLineItemsHelp ? 'true' : 'false'"
          @click="showLineItemsHelp = !showLineItemsHelp"
        >
          How this works
          <feather-icon :icon="showLineItemsHelp ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
        </button>
        <p v-if="showLineItemsHelp" class="li-howto-detail">
          Use <strong>Add item</strong> for each row. Click a summary row to expand its fields; click again to collapse.
          Preview shows a sample column layout. Edit Columns (gear) changes fields for this invoice.
        </p>
      </div>

      <b-alert
        :show="showLineItemErrors && lineItemErrorSummary.length > 0"
        variant="danger"
        class="py-2 px-3 mb-2"
      >
        <div class="d-flex align-items-start">
          <feather-icon icon="AlertCircleIcon" size="14" class="mr-1 mt-25 flex-shrink-0" />
          <div>
            <strong>{{ lineItemErrorSummary.length }} issue(s) to fix before continuing:</strong>
            <ul class="mb-0 pl-3 mt-1">
              <li v-for="msg in lineItemErrorSummary.slice(0, 6)" :key="msg">
                <a href="#" class="text-danger" @click.prevent="focusErrorFromMessage(msg)">{{ msg }}</a>
              </li>
            </ul>
            <span v-if="lineItemErrorSummary.length > 6" class="text-muted">…and {{ lineItemErrorSummary.length - 6 }} more.</span>
          </div>
        </div>
      </b-alert>

      <!-- Compact row overview -->
      <div class="li-summary mb-2">
        <div
          v-for="(item, index) in lineItems"
          :key="'sum-' + index"
          class="li-summary-block"
        >
          <div
            class="li-summary-row"
            :class="{
              'li-summary-row--active': index === activeRowIndex && editorExpanded,
              'li-summary-row--error': rowHasError(index),
            }"
            @click="selectRow(index)"
          >
            <span class="li-summary-num">{{ index + 1 }}</span>
            <div class="li-summary-main">
              <span class="li-summary-title">{{ rowTitle(item, index) }}</span>
              <span v-if="rowHasError(index)" class="li-summary-err">
                {{ firstRowError(index) }}
              </span>
            </div>
            <b-badge
              v-if="formData.useSecondLineItemsTable"
              :variant="Number(item.tableGroup) === 2 ? 'light-warning' : 'light-primary'"
              class="li-summary-table-badge"
            >
              {{ tableGroupLabel(item) }}
            </b-badge>
            <span class="li-summary-amount">{{ formatCurrency(rowAmount(item)) }}</span>
            <button
              type="button"
              class="icon-btn icon-btn-danger li-summary-remove"
              aria-label="Remove row"
              @click.stop="$emit('remove', index)"
            >
              <feather-icon icon="XIcon" size="14" />
            </button>
          </div>
          <div
            v-if="showOptionalTitleAsk(item, index)"
            class="li-title-ask"
            @click.stop
          >
            <span class="li-title-ask-text">Add a title to this line item?</span>
            <div class="li-title-ask-actions">
              <button type="button" class="li-title-text-btn" @click="acceptOptionalTitle(index)">
                Add title
              </button>
              <button type="button" class="li-title-text-btn li-title-text-btn--muted" @click="dismissOptionalTitle(item)">
                No thanks
              </button>
            </div>
          </div>
        </div>
        <div v-if="!lineItems.length" class="li-summary-empty text-muted">
          No line items yet. Click Add item to start.
        </div>
      </div>

      <div class="d-flex align-items-center flex-wrap mb-2 li-toolbar-actions">
        <b-button
          variant="primary"
          size="sm"
          class="li-add-item-btn mr-2"
          @click="onAddRow"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          Add item
        </b-button>
        <span
          v-if="formData.useSecondLineItemsTable"
          class="text-muted mr-2 li-add-table-hint"
        >
          Adds to {{ preferredTableGroupLabel }}
        </span>
        <template v-if="activeItem">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 wizard-icon-btn"
            aria-label="Move up"
            title="Move up"
            :disabled="activeRowIndex <= 0"
            @click="$emit('move-up', activeRowIndex); selectRow(Math.max(0, activeRowIndex - 1))"
          >
            <feather-icon icon="ChevronUpIcon" size="16" />
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-1 wizard-icon-btn"
            aria-label="Move down"
            title="Move down"
            :disabled="activeRowIndex >= lineItems.length - 1"
            @click="$emit('move-down', activeRowIndex); selectRow(Math.min(lineItems.length - 1, activeRowIndex + 1))"
          >
            <feather-icon icon="ChevronDownIcon" size="16" />
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="wizard-icon-btn"
            aria-label="Duplicate"
            title="Duplicate"
            @click="$emit('duplicate', activeRowIndex)"
          >
            <feather-icon icon="CopyIcon" size="16" />
          </b-button>
        </template>
      </div>

      <!-- Active row card form — all fields visible with labels -->
      <div v-if="activeItem" ref="activeCard" class="li-card">
        <div class="li-card-header">
          <span class="li-card-title">Item {{ activeRowIndex + 1 }} of {{ lineItems.length }}</span>
          <b-badge v-if="showLineItemErrors && rowHasError(activeRowIndex)" variant="light-danger">Needs attention</b-badge>
          <b-badge v-else-if="showLineItemErrors" variant="light-success">Ready</b-badge>
        </div>
        <div v-if="supportsOptionalTitle" class="li-optional-title">
          <template v-if="optionalTitleRevealed">
            <div class="d-flex align-items-center justify-content-between">
              <label class="li-field-label mb-0">Title <span class="text-muted font-weight-normal">(optional)</span></label>
              <button type="button" class="li-title-text-btn" @click="removeOptionalTitle">
                Remove title
              </button>
            </div>
            <v-select
              :value="optionalTitleValue(activeItem)"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="titleSelectOptions"
              :create-option="createTitleOption"
              taggable
              push-tags
              clearable
              placeholder="Select a title or type a custom one"
              @input="setOptionalTitle(activeItem, $event)"
            >
              <template #no-options="{ search }">
                <span v-if="search">Use "{{ search }}"</span>
                <span v-else>Pick a title or type a custom one.</span>
              </template>
            </v-select>
          </template>
          <div v-else-if="!isOptionalTitleDismissed(activeItem)" class="li-title-ask li-title-ask--card">
            <span class="li-title-ask-text">Add a title to this line item?</span>
            <div class="li-title-ask-actions">
              <button type="button" class="li-title-text-btn" @click="revealOptionalTitle">
                Add title
              </button>
              <button type="button" class="li-title-text-btn li-title-text-btn--muted" @click="dismissOptionalTitle(activeItem)">
                No thanks
              </button>
            </div>
          </div>
          <button
            v-else
            type="button"
            class="li-title-text-btn"
            @click="revealOptionalTitle"
          >
            Add title
          </button>
        </div>
        <div v-if="formData.useSecondLineItemsTable" class="li-field li-field--full px-2 pt-1">
          <label class="li-field-label">Which table should this row appear in?</label>
          <b-form-select
            :value="activeItem.tableGroup || 1"
            :options="tableGroupOptions"
            size="sm"
            @change="onTableGroupChange"
          />
          <small class="text-muted d-block mt-25">
            <template v-if="formData.secondTableColumnsSame === false">
              This row uses {{ tableGroupLabel(activeItem) }}’s columns.
            </template>
            <template v-else>
              Tables share the same columns; only the rows differ.
            </template>
          </small>
        </div>
        <div class="li-card-fields">
          <div
            v-for="col in editableColumns"
            :key="col.key"
            class="li-field"
            :class="{ 'li-field--error': cellError(activeRowIndex, col.key), 'li-field--full': isWideField(col) }"
            :data-field-key="col.key"
          >
            <label class="li-field-label">
              {{ col.label }}
              <span v-if="col.required && !isTitleSelectCol(col)" class="text-danger">*</span>
              <b-badge v-if="isComputedCol(col)" variant="light-info" class="ml-50 li-computed-badge">Auto</b-badge>
            </label>

            <template v-if="isSrNoColumn(col.key)">
              <b-form-input :value="activeRowIndex + 1" readonly size="sm" class="bg-light" />
            </template>

            <template v-else-if="isTitleSelectCol(col)">
              <v-select
                :value="titleSelectValue(activeItem, col.key)"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="titleSelectOptions"
                :create-option="createTitleOption"
                taggable
                push-tags
                clearable
                placeholder="Select a title (optional)"
                :class="{ 'is-invalid-vs': cellError(activeRowIndex, col.key) }"
                @input="setTitleValue(activeItem, col.key, $event)"
              >
                <template #no-options="{ search }">
                  <span v-if="search">Use "{{ search }}"</span>
                  <span v-else>Optional — pick a title or type a custom one.</span>
                </template>
              </v-select>
              <small class="text-muted d-block mt-25">Optional — leave blank if you don't need a title.</small>
            </template>

            <template v-else-if="usesSacSelect(col)">
              <v-select
                :value="sacHsnValue(activeItem)"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sacCodeOptions"
                label="text"
                :reduce="o => (typeof o === 'string' ? o : (o && o.value !== undefined ? o.value : o))"
                :create-option="createSacHsnOption"
                taggable
                push-tags
                :placeholder="col.label || 'Select or type a SAC / HSN Code'"
                :class="{ 'is-invalid-vs': cellError(activeRowIndex, col.key) }"
                @input="setSacHsnValue(activeItem, col.key, $event)"
              >
                <template #no-options="{ search }">
                  <span v-if="search">Use "{{ search }}"</span>
                  <span v-else>Type a code — no SAC/HSN options loaded yet.</span>
                </template>
              </v-select>
            </template>

            <template v-else-if="isComputedCol(col)">
              <div class="computed-amount-cell">
                {{ formatComputedCell(activeItem[col.key], col) }}
              </div>
              <small v-if="displayFormulaText(col.formula)" class="text-muted d-block li-formula-hint">ƒ {{ displayFormulaText(col.formula) }}</small>
              <small v-else class="text-muted d-block li-formula-hint">Auto-calculated</small>
            </template>

            <!-- Manual Amount / Base Amount when Computed is off and type is still currency -->
            <template v-else-if="isManualCurrencyAmount(col)">
              <b-input-group size="sm" class="currency-input-group">
                <b-input-group-prepend>
                  <span class="input-group-text">₹</span>
                </b-input-group-prepend>
                <b-form-input
                  :ref="'field-' + col.key"
                  v-model.number="activeItem[col.key]"
                  type="number"
                  min="0"
                  step="0.01"
                  class="text-right font-weight-bold"
                  :state="cellState(activeRowIndex, col.key)"
                  @input="onManualAmountInput(col.key)"
                />
              </b-input-group>
            </template>

            <template v-else-if="['number', 'percentage'].includes(col.type)">
              <b-input-group v-if="col.type === 'percentage'" size="sm">
                <b-form-input
                  :ref="'field-' + col.key"
                  v-model.number="activeItem[col.key]"
                  type="number"
                  min="0"
                  step="0.01"
                  class="text-right"
                  :state="cellState(activeRowIndex, col.key)"
                  @input="$emit('recompute-row', activeRowIndex)"
                />
                <b-input-group-append>
                  <span class="input-group-text">%</span>
                </b-input-group-append>
              </b-input-group>
              <b-form-input
                v-else
                :ref="'field-' + col.key"
                v-model.number="activeItem[col.key]"
                type="number"
                min="0"
                step="0.01"
                size="sm"
                class="text-right"
                :state="cellState(activeRowIndex, col.key)"
                @input="$emit('recompute-row', activeRowIndex)"
              />
            </template>

            <template v-else-if="col.type === 'currency'">
              <b-input-group size="sm" class="currency-input-group">
                <b-input-group-prepend>
                  <span class="input-group-text">₹</span>
                </b-input-group-prepend>
                <b-form-input
                  :ref="'field-' + col.key"
                  v-model.number="activeItem[col.key]"
                  type="number"
                  min="0"
                  step="0.01"
                  class="text-right"
                  :state="cellState(activeRowIndex, col.key)"
                  @input="$emit('recompute-row', activeRowIndex)"
                />
              </b-input-group>
            </template>

            <template v-else-if="col.type === 'date'">
              <flat-pickr
                v-model="activeItem[col.key]"
                class="form-control form-control-sm"
                :config="{ enableTime: false, dateFormat: 'd-M-y' }"
                :placeholder="col.label"
              />
            </template>

            <template v-else>
              <b-form-input
                :ref="'field-' + col.key"
                v-model="activeItem[col.key]"
                size="sm"
                :state="cellState(activeRowIndex, col.key)"
                :placeholder="col.label"
              />
            </template>

            <small
              class="d-block cell-error-text"
              :class="{ 'text-danger': cellError(activeRowIndex, col.key) }"
            >{{ cellError(activeRowIndex, col.key) }}</small>
          </div>
        </div>
      </div>
      </b-collapse>
    </div>

    <!-- Amount Summary — totals + GST Type select + Decimals toggle -->
    <div class="wizard-question wizard-accordion-section mb-0">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'totals' ? 'true' : 'false'"
        @click="toggleSection('totals')"
      >
        <h5 class="wizard-question-heading">Invoice totals</h5>
        <feather-icon :icon="openSection === 'totals' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'totals'">
      <div class="amount-summary-toolbar">
        <div class="amount-summary-gst">
          <label for="amount-summary-gst-type" class="amount-summary-label">GST Type</label>
          <b-form-select
            id="amount-summary-gst-type"
            v-model="formData.gstType"
            :options="gstTypeSelectOptions"
            class="gst-type-inline-select"
            aria-label="GST Type for invoice totals"
          />
        </div>
        <div class="amount-summary-format" role="group" aria-label="Number format">
          <span class="amount-summary-label">Format</span>
          <div class="amount-summary-toggles">
            <b-form-checkbox
              :checked="showDecimals"
              switch
              @change="$emit('show-decimals-changed', $event)"
            >
              Decimals
            </b-form-checkbox>
            <b-form-checkbox
              :checked="showCurrencySymbol"
              switch
              @change="$emit('show-currency-symbol-changed', $event)"
            >
              ₹ symbol
            </b-form-checkbox>
            <b-form-checkbox
              :checked="useThousandSeparators"
              switch
              @change="$emit('use-thousand-separators-changed', $event)"
            >
              Commas
            </b-form-checkbox>
          </div>
        </div>
      </div>
      <b-card class="amount-summary-card mb-0" body-class="py-2 px-3">
        <div class="amount-summary-rows">
          <div class="amount-summary-row">
            <span class="amount-summary-row-label">Subtotal</span>
            <span class="amount-summary-row-value">{{ formatCurrency(subtotal) }}</span>
          </div>
          <div class="amount-summary-row">
            <span class="amount-summary-row-label">GST Amount</span>
            <span class="amount-summary-row-value">{{ formatCurrency(gstAmount) }}</span>
          </div>
          <div v-if="formData.tdsApplicable" class="amount-summary-row">
            <span class="amount-summary-row-label">TDS Amount</span>
            <span class="amount-summary-row-value">&minus; {{ formatCurrency(tdsAmount) }}</span>
          </div>
          <hr class="amount-summary-divider" />
          <div class="amount-summary-row amount-summary-row-total">
            <span class="amount-summary-row-label">Total Amount</span>
            <span class="amount-summary-total">{{ formatCurrency(totalAmount) }}</span>
          </div>
        </div>
      </b-card>
      </b-collapse>
    </div>

    <div class="wizard-nav-actions d-flex justify-content-between align-items-center w-100 flex-wrap">
      <b-button
        variant="outline-secondary"
        size="sm"
        class="wizard-icon-btn"
        aria-label="Back"
        @click="$emit('back')"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" />
      </b-button>
      <b-button
        variant="primary"
        size="sm"
        class="wizard-icon-btn"
        aria-label="Next: More Details"
        @click="onNext"
      >
        <feather-icon icon="ArrowRightIcon" size="16" />
      </b-button>
      <span v-if="proceedBlockReason" class="text-danger wizard-nav-error">{{ proceedBlockReason }}</span>
    </div>
  </div>
</template>

<script>
import {
  BCard, BFormGroup, BFormInput, BFormCheckbox, BFormSelect, BBadge, BButton,
  BFormRadioGroup, BAlert, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BSpinner,
  BCollapse,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { formatCurrency as formatCurrencyUtil, formatLineItemCellValue } from '@/utils/currency-format'
import {
  resolveSacHsnCode,
  migrateLineItemSacHsn,
  isComputedColumn,
  isTitleSelectColumn,
  buildLineItemTitleOptions,
  resolveLineItemTitle,
  supportsOptionalLineItemTitle,
  resolveOptionalOverlayTitle,
} from '@/utils/invoice-calculation-engine'
import {
  displayFormula,
  usesSacSelectWidget,
  usesCurrencyAmountWidget,
} from '@/utils/invoice-create-columns'

const SYSTEM_TYPES = new Set(['move', 'actions'])
const TITLE_KEYS = [
  'title', 'itemName', 'description', 'employeeName', 'candidateName', 'descriptionOfGoods',
]
const AMOUNT_KEYS = ['lineTotal', 'amount', 'baseAmount', 'taxableValue']

export default {
  name: 'WizardLineItemsStep',
  components: {
    BCard, BFormGroup, BFormInput, BFormCheckbox, BFormSelect, BBadge, BButton,
    BFormRadioGroup, BAlert, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BSpinner,
    BCollapse, vSelect, flatPickr,
  },
  props: {
    formData: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
    lineItems: { type: Array, default: () => [] },
    // Kept for parent compatibility (table fields); card form uses column specs.
    effectiveLineItemFields: { type: Array, default: () => [] },
    lineItemColumnSpecs: { type: Array, default: () => [] },
    /** When second table has different columns, Table 2 field specs. */
    lineItemColumnSpecsTable2: { type: Array, default: () => [] },
    lineItemErrors: { type: Array, default: () => [] },
    lineItemErrorSummary: { type: Array, default: () => [] },
    selectedCandidateIds: { type: Array, default: () => [] },
    sacCodeOptions: { type: Array, default: () => [] },
    verticalOptions: { type: Array, default: () => [] },
    isCandidateCategory: Boolean,
    isForCandidates: { type: Boolean, default: null },
    isRpoTemplate: Boolean,
    activeTemplateName: { type: String, default: 'Standard' },
    subtotal: { type: Number, default: 0 },
    gstAmount: { type: Number, default: 0 },
    tdsAmount: { type: Number, default: 0 },
    totalAmount: { type: Number, default: 0 },
    canProceed: Boolean,
    templateLoading: { type: Boolean, default: false },
    showDecimals: { type: Boolean, default: true },
    showCurrencySymbol: { type: Boolean, default: true },
    useThousandSeparators: { type: Boolean, default: true },
    gstTypeOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      isForCandidatesLocal: this.isForCandidates,
      activeRowIndex: 0,
      // Collapsed by default until the user clicks a summary row (or Add Item).
      editorExpanded: false,
      // Only show the pink issues banner + field red outlines after the user
      // tries Next (or stays on the step with missing required data after that).
      // Empty/default rows on first landing must not look like errors.
      showLineItemErrors: false,
      showCategoryVerticalErrors: false,
      showLineItemsHelp: false,
      optionalTitleOpen: false,
      // Rows where the user said “No thanks” — keep asking other rows.
      optionalTitleDismissedItems: [],
      openSection: this.formData && this.formData.category && this.formData.vertical
        ? 'lineItems'
        : 'category',
    }
  },
  watch: {
    isForCandidates(v) { this.isForCandidatesLocal = v },
    'formData.category'() {
      this.syncCategorySection()
    },
    'formData.vertical'() {
      this.syncCategorySection()
    },
    lineItems: {
      handler(items) {
        if (!items || !items.length) {
          this.activeRowIndex = 0
          this.editorExpanded = false
          this.optionalTitleOpen = false
          this.optionalTitleDismissedItems = []
          return
        }
        if (this.activeRowIndex >= items.length) {
          this.activeRowIndex = items.length - 1
        }
        this.optionalTitleDismissedItems = this.optionalTitleDismissedItems.filter(
          (item) => items.indexOf(item) !== -1,
        )
      },
      deep: false,
    },
    activeRowIndex() {
      this.optionalTitleOpen = false
    },
  },
  computed: {
    gstTypeSelectOptions() {
      // Placeholder only when unset — once chosen, do not re-offer an empty
      // "Select GST type" option that looks like the field is still blank.
      const chosen = this.gstTypeOptions.map((o) => ({ value: o.title, text: o.label || o.title }))
      if (this.formData && this.formData.gstType) return chosen
      return [{ value: '', text: 'Select GST type' }, ...chosen]
    },
    secondTableColumnsSameOptions() {
      return [
        { text: 'Yes — same columns', value: true },
        { text: 'No — different columns', value: false },
      ]
    },
    usesSeparateTableColumns() {
      return !!(this.formData.useSecondLineItemsTable && this.formData.secondTableColumnsSame === false)
    },
    /** Column specs for the active row’s table (1 or 2). */
    activeRowColumnSpecs() {
      return this.columnSpecsForItem(this.lineItems[this.activeRowIndex] || this.activeItem)
    },
    supportsOptionalTitle() {
      return supportsOptionalLineItemTitle(this.activeRowColumnSpecs)
    },
    optionalTitleRevealed() {
      if (!this.activeItem) return false
      return this.optionalTitleOpen || !!resolveOptionalOverlayTitle(this.activeItem)
    },
    editableColumns() {
      return (this.activeRowColumnSpecs || []).filter(
        (c) => c && !SYSTEM_TYPES.has(c.type) && c.visible !== false,
      )
    },
    activeItem() {
      if (!this.editorExpanded || !this.lineItems.length) return null
      return this.lineItems[this.activeRowIndex] || null
    },
    isCategoryVerticalValid() {
      return !!(this.formData.category && this.formData.vertical)
    },
    canCollapseCategory() {
      return this.isCategoryVerticalValid
    },
    isCategorySectionOpen() {
      return !this.isCategoryVerticalValid || this.openSection === 'category'
    },
    categoryVerticalErrorMessage() {
      if (this.isCategoryVerticalValid) return ''
      if (!this.formData.category && !this.formData.vertical) {
        return 'Please select category and vertical.'
      }
      if (!this.formData.category) return 'Please select a category.'
      return 'Please select a vertical.'
    },
    proceedBlockReason() {
      if (!this.isCategoryVerticalValid) return this.categoryVerticalErrorMessage
      if (!this.canProceed) return 'Add at least one valid line item to continue.'
      if (this.formData.useSecondLineItemsTable) {
        const h1 = String(this.formData.lineItemTableHeading1 || '').trim()
        const h2 = String(this.formData.lineItemTableHeading2 || '').trim()
        if (!h1 || !h2) return 'Enter headings for both line items tables.'
      }
      return ''
    },
    tableGroupOptions() {
      const h1 = String(this.formData.lineItemTableHeading1 || '').trim() || 'Table 1'
      const h2 = String(this.formData.lineItemTableHeading2 || '').trim() || 'Table 2'
      return [
        { value: 1, text: h1 },
        { value: 2, text: h2 },
      ]
    },
    preferredTableGroup() {
      if (!this.formData.useSecondLineItemsTable) return 1
      return Number((this.activeItem && this.activeItem.tableGroup) || 1) === 2 ? 2 : 1
    },
    preferredTableGroupLabel() {
      return this.tableGroupLabel({ tableGroup: this.preferredTableGroup })
    },
    titleSelectOptions() {
      return buildLineItemTitleOptions({
        sacCodeOptions: this.sacCodeOptions,
        lineItems: this.lineItems,
      })
    },
  },
  methods: {
    toggleSection(key) {
      this.openSection = this.openSection === key ? '' : key
    },
    toggleCategorySection() {
      if (!this.canCollapseCategory) {
        this.showCategoryVerticalErrors = true
        return
      }
      this.toggleSection('category')
    },
    syncCategorySection() {
      if (!this.isCategoryVerticalValid) {
        this.openSection = 'category'
        return
      }
      if (this.openSection === 'category') this.openSection = 'lineItems'
    },
    formatCurrency(val) {
      return formatCurrencyUtil(val, this.showDecimals ? 2 : 0, {
        showCurrencySymbol: this.showCurrencySymbol,
        useThousandSeparators: this.useThousandSeparators,
      })
    },
    formatComputedCell(val, col) {
      return formatLineItemCellValue(val, col, {
        decimals: this.showDecimals ? 2 : 0,
        showCurrencySymbol: this.showCurrencySymbol,
        useThousandSeparators: this.useThousandSeparators,
      })
    },
    tableGroupLabel(item) {
      const group = Number(item && item.tableGroup) === 2 ? 2 : 1
      const opt = this.tableGroupOptions.find((o) => o.value === group)
      return (opt && opt.text) || (group === 2 ? 'Table 2' : 'Table 1')
    },
    onTableGroupChange(val) {
      if (!this.activeItem) return
      this.$set(this.activeItem, 'tableGroup', Number(val) === 2 ? 2 : 1)
      // Different column sets: recompute against the destination table’s formulas.
      this.$emit('recompute-row', this.activeRowIndex)
    },
    // SAC / HSN is one field — read any legacy SAC/HSN alias (hsn, hsnCode…).
    sacHsnValue(item) {
      return resolveSacHsnCode(item) || null
    },
    createSacHsnOption(search) {
      const code = String(search || '').trim()
      return { text: code, value: code }
    },
    setSacHsnValue(item, _key, val) {
      if (!item) return
      const code = val != null && String(val).trim() !== '' ? String(val).trim() : ''
      this.$set(item, 'sacCode', code)
      migrateLineItemSacHsn(item)
      this.$emit('recompute-row', this.activeRowIndex)
    },
    isTitleSelectCol(col) {
      return isTitleSelectColumn(col, this.activeRowColumnSpecs)
    },
    titleSelectValue(item, key) {
      if (!item) return null
      const raw = item[key] != null && String(item[key]).trim() !== ''
        ? item[key]
        : resolveLineItemTitle(item)
      return raw ? String(raw).trim() : null
    },
    createTitleOption(search) {
      return String(search || '').trim()
    },
    setTitleValue(item, key, val) {
      if (!item) return
      const title = val != null && String(val).trim() !== '' ? String(val).trim() : ''
      this.$set(item, key, title)
      this.$set(item, 'title', title)
      if (key !== 'itemName' && !String(item.itemName || '').trim()) {
        this.$set(item, 'itemName', title)
      }
    },
    columnSpecsForItem(item) {
      if (
        this.usesSeparateTableColumns
        && Number((item && item.tableGroup) || 1) === 2
        && this.lineItemColumnSpecsTable2
        && this.lineItemColumnSpecsTable2.length
      ) {
        return this.lineItemColumnSpecsTable2
      }
      return this.lineItemColumnSpecs || []
    },
    rowSupportsOptionalTitle(item) {
      return supportsOptionalLineItemTitle(this.columnSpecsForItem(item))
    },
    isOptionalTitleDismissed(item) {
      return !!item && this.optionalTitleDismissedItems.indexOf(item) !== -1
    },
    showOptionalTitleAsk(item, index) {
      if (!item || !this.rowSupportsOptionalTitle(item)) return false
      if (resolveOptionalOverlayTitle(item)) return false
      if (this.isOptionalTitleDismissed(item)) return false
      // Expanded card shows the same ask for the active row.
      if (this.editorExpanded && index === this.activeRowIndex) return false
      return true
    },
    optionalTitleValue(item) {
      const title = resolveOptionalOverlayTitle(item)
      return title || null
    },
    setOptionalTitle(item, val) {
      if (!item) return
      const title = val != null && String(val).trim() !== '' ? String(val).trim() : ''
      this.$set(item, 'title', title)
    },
    clearOptionalTitleDismissed(item) {
      const idx = this.optionalTitleDismissedItems.indexOf(item)
      if (idx !== -1) this.optionalTitleDismissedItems.splice(idx, 1)
    },
    revealOptionalTitle() {
      this.clearOptionalTitleDismissed(this.activeItem)
      this.optionalTitleOpen = true
    },
    acceptOptionalTitle(index) {
      const item = this.lineItems[index]
      this.clearOptionalTitleDismissed(item)
      this.selectRow(index)
      this.$nextTick(() => {
        this.optionalTitleOpen = true
      })
    },
    dismissOptionalTitle(item) {
      if (!item || this.isOptionalTitleDismissed(item)) return
      this.optionalTitleDismissedItems.push(item)
      if (item === this.activeItem) this.optionalTitleOpen = false
    },
    removeOptionalTitle() {
      if (this.activeItem) this.$set(this.activeItem, 'title', '')
      this.optionalTitleOpen = false
    },
    getColSpec(key) {
      return this.activeRowColumnSpecs.find((c) => c.key === key) || { key, label: key, type: 'text', required: false }
    },
    cellError(index, key) {
      if (!this.showLineItemErrors) return ''
      const row = this.lineItemErrors[index]
      return row ? row[key] || '' : ''
    },
    cellState(index, key) {
      return this.cellError(index, key) ? false : null
    },
    isSrNoColumn(key) {
      const spec = this.getColSpec(key)
      const k = String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      if (k === 'srno' || k === 'sno' || k === 'slno') return true
      const l = String(spec.label || '').toLowerCase()
      return /\bsr\.?\s*no\.?\b|\bsl\.?\s*no\.?\b|serial\s*no/.test(l)
    },
    isComputedCol(col) {
      return isComputedColumn(col)
    },
    usesSacSelect(col) {
      return usesSacSelectWidget(col)
    },
    displayFormulaText(formula) {
      return displayFormula(formula)
    },
    isManualCurrencyAmount(col) {
      return usesCurrencyAmountWidget(col) && (col.key === 'amount' || col.key === 'baseAmount')
    },
    /** Manual Amount / Base Amount (Computed off) — keep sibling totals in sync. */
    onManualAmountInput(key) {
      if (key === 'baseAmount' && this.isRpoTemplate) {
        this.$emit('update-rpo-amount', this.activeRowIndex)
        return
      }
      this.$emit('recompute-row', this.activeRowIndex)
    },
    isWideField(col) {
      return col.type === 'text' || col.type === 'title-select' || col.key === 'itemName' || col.key === 'title'
        || /name|description|goods|remarks/i.test(col.key || '')
    },
    rowHasError(index) {
      if (!this.showLineItemErrors) return false
      const row = this.lineItemErrors[index]
      return !!(row && Object.keys(row).length)
    },
    firstRowError(index) {
      if (!this.showLineItemErrors) return ''
      const row = this.lineItemErrors[index]
      if (!row) return ''
      const key = Object.keys(row)[0]
      const col = this.getColSpec(key)
      return `${col.label || key} — ${row[key]}`
    },
    rowTitle(item, index) {
      if (!item) return `Item ${index + 1}`
      const overlay = resolveOptionalOverlayTitle(item)
      const name = String(item.candidateName || item.employeeName || item.itemName || '').trim()
      if (this.supportsOptionalTitle && overlay && name && overlay !== name) {
        return `${overlay} · ${name}`
      }
      for (let i = 0; i < TITLE_KEYS.length; i += 1) {
        const k = TITLE_KEYS[i]
        if (item[k] != null && String(item[k]).trim()) return String(item[k]).trim()
      }
      // First non-empty text-ish column
      const textCol = this.editableColumns.find((c) => {
        if (this.isSrNoColumn(c.key) || isComputedColumn(c)) return false
        if (['number', 'currency', 'percentage', 'date', 'sac-select'].includes(c.type)) return false
        return item[c.key] != null && String(item[c.key]).trim()
      })
      if (textCol) return String(item[textCol.key]).trim()
      return `Item ${index + 1}`
    },
    rowAmount(item) {
      if (!item) return 0
      for (let i = 0; i < AMOUNT_KEYS.length; i += 1) {
        const k = AMOUNT_KEYS[i]
        if (item[k] != null && item[k] !== '') return parseFloat(item[k]) || 0
      }
      return 0
    },
    selectRow(index) {
      if (index < 0 || index >= this.lineItems.length) return
      // Click active summary again to collapse the editor (progressive disclosure).
      if (this.activeRowIndex === index && this.editorExpanded) {
        this.editorExpanded = false
        return
      }
      this.activeRowIndex = index
      this.editorExpanded = true
      this.$nextTick(() => {
        const card = this.$refs.activeCard
        if (card && card.scrollIntoView) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    },
    onAddRow() {
      const preferredGroup = this.preferredTableGroup
      this.$emit('add-row', preferredGroup)
      this.$nextTick(() => {
        const idx = Math.max(0, this.lineItems.length - 1)
        this.selectRow(idx)
      })
    },
    focusErrorFromMessage(msg) {
      // "Row N: Label — message"
      const m = String(msg || '').match(/^Row\s+(\d+)\s*:/i)
      if (m) {
        const idx = parseInt(m[1], 10) - 1
        this.selectRow(idx)
        this.$nextTick(() => this.focusFirstErrorInRow(idx))
      } else {
        this.focusFirstError()
      }
    },
    focusFirstError() {
      const idx = (this.lineItemErrors || []).findIndex((r) => r && Object.keys(r).length)
      if (idx < 0) return
      this.selectRow(idx)
      this.$nextTick(() => this.focusFirstErrorInRow(idx))
    },
    focusFirstErrorInRow(index) {
      const row = this.lineItemErrors[index]
      if (!row) return
      const key = Object.keys(row)[0]
      const el = this.$el && this.$el.querySelector(`[data-field-key="${key}"] input, [data-field-key="${key}"] .vs__search`)
      if (el && el.focus) {
        el.focus()
        if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    onCategoryInput(value) {
      this.$emit('category-selected', value)
      if (value && this.formData.vertical) this.showCategoryVerticalErrors = false
    },
    onVerticalInput(value) {
      if (value && this.formData.category) this.showCategoryVerticalErrors = false
    },
    onNext() {
      // Category + vertical are required before leaving this step.
      if (!this.isCategoryVerticalValid) {
        this.showCategoryVerticalErrors = true
        return
      }
      if (this.formData.useSecondLineItemsTable) {
        const h1 = String(this.formData.lineItemTableHeading1 || '').trim()
        const h2 = String(this.formData.lineItemTableHeading2 || '').trim()
        if (!h1 || !h2) return
      }
      // Validation attempt — surface banner + field errors only from here on
      // while required data is still missing; they clear once rows are valid.
      this.showLineItemErrors = true
      if (!this.canProceed) {
        if (this.lineItemErrorSummary && this.lineItemErrorSummary.length) {
          this.focusFirstError()
        }
        return
      }
      this.$emit('next')
    },
  },
}
</script>

<style src="./wizard-step-shared.css"></style>
<style scoped>
.category-quick-pick-row {
  gap: 0.65rem;
}
.category-quick-pick-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 140px;
  min-width: 0;
}
.category-inline-select {
  min-width: 0;
  width: 100%;
}
.category-inline-select.is-invalid-vs >>> .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}
.wizard-field-error {
  font-size: 0.875rem;
}
.line-items-section small:not(.text-danger),
.li-field small:not(.text-danger) {
  font-size: 0.8125rem;
}
.preset-name-badge {
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 0.3rem 0.55rem;
}

.line-items-section {
  margin-top: 0.5rem;
  margin-bottom: 1.15rem;
}

.line-items-section >>> .form-control,
.line-items-section >>> .custom-select {
  height: 36px !important;
  min-height: 36px !important;
  padding: 0.35rem 0.6rem !important;
  font-size: 0.875rem !important;
}
.line-items-section >>> .form-control:focus,
.line-items-section >>> .custom-select:focus {
  border-color: #00AC99 !important;
  box-shadow: 0 0 0 1px rgba(0, 172, 153, 0.15);
}
.line-items-section >>> .form-control.is-invalid,
.line-items-section >>> .form-control.is-invalid:focus {
  border-color: #ea5455 !important;
  background-image: none;
  padding-right: 0.5rem !important;
}
.line-items-section >>> .v-select .vs__dropdown-toggle {
  min-height: 36px !important;
  padding: 0 4px !important;
}
.line-items-section >>> .v-select .vs__search,
.line-items-section >>> .v-select .vs__selected {
  font-size: 0.875rem;
  padding: 0 2px;
}

.cell-error-text {
  display: block;
  min-height: 1.05rem;
  font-size: 0.8125rem;
  line-height: 1.3;
  white-space: normal;
}

.currency-input-group {
  align-items: stretch;
}
.currency-input-group >>> .input-group-text,
.li-field >>> .input-group-text {
  height: 36px;
  min-height: 36px;
  padding: 0.35rem 0.55rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #5e5873;
  background-color: #f8f8f8;
}

.computed-amount-cell {
  height: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.35rem 0.6rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2e3030;
  background-color: #f4f6f5;
  border: 1px dashed #d8d6de;
  border-radius: 0.25rem;
  cursor: help;
}
.li-formula-hint {
  font-size: 0.75rem;
  margin-top: 0.15rem;
  color: #6e6b7b;
}
.li-computed-badge {
  font-size: 0.7rem;
  font-weight: 700;
  vertical-align: middle;
}

/* Compact summary list */
.li-summary {
  border: 1px solid #ebe9f1;
  border-radius: 0.45rem;
  overflow: hidden;
  background: #fff;
  max-height: 260px;
  overflow-y: auto;
}
.li-summary-block + .li-summary-block {
  border-top: 1px solid #f0eff4;
}
.li-summary-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.li-summary-row:hover {
  background: rgba(0, 172, 153, 0.04);
}
.li-summary-row--active {
  background: rgba(0, 172, 153, 0.1);
  box-shadow: inset 3px 0 0 #00AC99;
}
.li-summary-row--error {
  background: rgba(234, 84, 85, 0.04);
}
.li-summary-row--active.li-summary-row--error {
  background: rgba(234, 84, 85, 0.08);
  box-shadow: inset 3px 0 0 #ea5455;
}
.li-summary-num {
  flex-shrink: 0;
  width: 1.65rem;
  height: 1.65rem;
  border-radius: 50%;
  background: #f4f4f6;
  color: #5e5873;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.li-summary-row--active .li-summary-num {
  background: #eefaf8;
  color: #00AC99;
}
.li-summary-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.li-summary-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #2f2b3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.li-summary-err {
  font-size: 0.75rem;
  color: #ea5455;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.li-summary-table-badge {
  flex-shrink: 0;
  max-width: 7.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 600;
}
.li-summary-amount {
  flex-shrink: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2e3030;
  font-variant-numeric: tabular-nums;
}
.li-add-table-hint {
  font-size: 0.8125rem;
  line-height: 1.3;
}
.li-summary-remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: #6e6b7b;
  cursor: pointer;
}
.li-summary-remove:hover {
  background-color: rgba(234, 84, 85, 0.1);
  color: #ea5455;
}
.li-summary-empty {
  padding: 0.9rem 0.85rem;
  text-align: center;
  font-size: 0.875rem;
}

/* Active item card form */
.li-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: linear-gradient(180deg, #fbfbfd 0%, #fff 48px);
  padding: 0.75rem 0.85rem 0.5rem;
}
.li-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
  padding-bottom: 0.45rem;
  border-bottom: 1px solid #ebe9f1;
}
.li-optional-title {
  margin-bottom: 0.65rem;
}
.li-title-ask {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
  padding: 0.25rem 0.75rem 0.55rem 2.75rem;
}
.li-title-ask--card {
  padding: 0;
}
.li-title-ask-text {
  font-size: 0.875rem;
  color: #5e5873;
}
.li-title-ask-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.li-title-text-btn {
  display: inline-block;
  padding: 0.15rem 0;
  border: none;
  background: none;
  color: #00AC99;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
}
.li-title-text-btn:hover,
.li-title-text-btn:focus {
  color: #008f80;
  text-decoration: underline;
}
.li-title-text-btn--muted {
  color: #6e6b7b;
  font-weight: 500;
}
.li-title-text-btn--muted:hover,
.li-title-text-btn--muted:focus {
  color: #5e5873;
}
.li-card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2f2b3d;
  flex: 1 1 auto;
}
.li-card-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem 0.75rem;
}
.li-field {
  min-width: 0;
}
.li-field--full {
  grid-column: 1 / -1;
}
.li-field--error .li-field-label {
  color: #ea5455;
}
.li-field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #3d3d3d;
  margin-bottom: 0.25rem;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
}
.li-add-item-btn.btn {
  display: inline-flex;
  align-items: center;
  min-height: 2.25rem;
  padding: 0.4rem 0.85rem;
  font-size: 0.875rem;
  font-weight: 600;
}
.li-toolbar-actions .btn-sm {
  line-height: 1;
}

.li-howto-lead {
  margin-bottom: 0.15rem;
}
.li-howto-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-top: 0.2rem;
  padding: 0.2rem 0;
  border: none;
  background: none;
  color: #00AC99;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
}
.li-howto-toggle:hover,
.li-howto-toggle:focus {
  color: #008f80;
  text-decoration: underline;
}
.li-howto-detail {
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
}

.amount-summary-card {
  border: 1px solid #ebe9f1;
  border-left: 3px solid #00AC99;
  background-color: #f8f8fb;
}
.amount-summary-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1.5rem;
  margin: 0.15rem 0 0.75rem;
}
.amount-summary-gst,
.amount-summary-format {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.amount-summary-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #3d3d3d;
  margin: 0;
  line-height: 1.3;
}
.amount-summary-toggles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  min-height: 34px;
}
.gst-type-inline-select {
  width: auto;
  min-width: 10rem;
  height: 36px !important;
  min-height: 36px !important;
  padding: 0.3rem 1.75rem 0.3rem 0.6rem !important;
  font-size: 0.875rem !important;
}
.amount-summary-rows {
  font-size: 0.9375rem;
  line-height: 1.35;
}
.amount-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  padding: 0.35rem 0;
}
.amount-summary-row-label {
  color: #5e5873;
}
.amount-summary-row-value {
  font-weight: 600;
  color: #2f2b3d;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.amount-summary-row-total {
  padding-top: 0.4rem;
}
.amount-summary-row-total .amount-summary-row-label {
  font-weight: 700;
  color: #2f2b3d;
}
.amount-summary-divider {
  margin: 0.15rem 0 0.2rem;
  border-top: 1px dashed #d8d6de;
}
.amount-summary-total {
  font-size: 1.125rem;
  font-weight: 700;
  color: #00AC99;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.wizard-nav-error {
  flex-basis: 100%;
  width: 100%;
  font-size: 0.875rem;
  margin-top: 0.4rem;
}

@media (max-width: 420px) {
  .li-card-fields {
    grid-template-columns: 1fr;
  }
  .li-field--full {
    grid-column: auto;
  }
}
</style>
